// Pure resolver mapping a location to a WORKBENCH session descriptor.
//
// Only three kinds of resource become live Workbench sessions: a semantic
// MODEL editor, an INSIGHT editor/viewer, and an AI EXPLORE session. Every
// other route is a plain screen and resolves to `null` (so navigating around
// the app never spawns sessions). A descriptor is
// { key, type, title, path, pinned }. `key` is the singleton identity — the
// same resource always maps to the same key, so opening it again focuses the
// existing session instead of duplicating it.

export function resolveWorkspaceTab(pathname, search) {
  if (!pathname) return null;
  const params = new URLSearchParams(search || '');

  // ── MODEL: editing a specific semantic model (or its aggregates) ──────────
  const aggMatch = pathname.match(/^\/semantic-models\/([^/]+)\/aggregates$/);
  if (aggMatch) {
    const id = aggMatch[1];
    return { key: `model:${id}`, type: 'model', title: 'Model', path: pathname };
  }
  if (pathname === '/semantic-models') {
    const model = params.get('model');
    if (model) return { key: `model:${model}`, type: 'model', title: 'Model', path: `/semantic-models?model=${model}` };
    return null; // the model LIST is just a screen
  }

  // ── INSIGHT: viewing / building / visually editing an insight ─────────────
  if (pathname === '/dashboards/view' || pathname === '/reports/view'
      || pathname === '/dashboards/builder' || pathname === '/reports/builder') {
    const id = params.get('id');
    if (id) return { key: `insight:${id}`, type: 'insight', title: 'Insight', path: `${pathname}?id=${id}` };
    // Each brand-new report carries a unique ?draft=<token> so several can be
    // open at once (its own Workbench chip + its own draft), instead of all
    // collapsing into one shared "insight:new" session.
    const draft = params.get('draft');
    if (draft) return { key: `insight:draft:${draft}`, type: 'insight', title: 'New Insight', path: `${pathname}?draft=${draft}` };
    return { key: 'insight:new', type: 'insight', title: 'New Insight', path: pathname };
  }
  const createMatch = pathname.match(/^\/dashboards\/create\/([^/]+)$/);
  if (createMatch) {
    return { key: `insight:${createMatch[1]}`, type: 'insight', title: 'Insight', path: pathname };
  }
  if (pathname === '/dashboards/create') {
    return { key: 'insight:new', type: 'insight', title: 'New Insight', path: pathname };
  }
  if (pathname === '/visual-library/edit' || pathname === '/visual-library/preview') {
    const id = params.get('id') || params.get('visual');
    const key = id ? `insight:visual:${id}` : `insight:visual:${pathname}`;
    return { key, type: 'insight', title: 'Insight', path: pathname + (search || '') };
  }

  // ── EXPLORE: an AI Explore session (one per model). The bare model-picker
  // (no ?model=) is just a screen, so it doesn't add a redundant session. ─────
  if (pathname === '/ai-copilot') {
    const model = params.get('model');
    if (model) {
      const s = params.get('s');
      const key = `explore:${model}`;
      const path = `/ai-copilot?model=${model}${s ? `&s=${s}` : ''}`;
      return { key, type: 'explore', title: 'AI Explore', path };
    }
    return null;
  }

  // Everything else is a plain screen — no session.
  return null;
}
